import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, RefreshCw, Edit2, Trash2, X, Check,
  Link2, Unlink, BarChart2, Calendar, DollarSign,
  Users, Target, MessageSquare, Radio,
} from 'lucide-react';
import {
  projectPromotionsService,
  PROMOTION_STATUSES,
  COMMON_CHANNELS,
} from '../api/projectPromotionsApi';
import { BRAND_COLORS, NEUTRAL_COLORS, SYSTEM_COLORS } from '../../../constants/brandPalette';
import { toast } from 'react-hot-toast';

// ── Meta ──────────────────────────────────────────────────────────────────────
const STATUS_META = {
  planning:  { label: 'Planning',  bg: SYSTEM_COLORS.surfaceMuted,  color: SYSTEM_COLORS.textMuted  },
  active:    { label: 'Active',    bg: SYSTEM_COLORS.successTint,   color: SYSTEM_COLORS.success    },
  paused:    { label: 'Paused',    bg: SYSTEM_COLORS.warningTint,   color: SYSTEM_COLORS.warning    },
  completed: { label: 'Completed', bg: SYSTEM_COLORS.infoTint,      color: SYSTEM_COLORS.info       },
  cancelled: { label: 'Cancelled', bg: SYSTEM_COLORS.dangerTint,    color: SYSTEM_COLORS.danger     },
};

const CHANNEL_COLORS = {
  instagram: { bg: '#fce7f3', color: '#be185d' },
  facebook:  { bg: '#dbeafe', color: '#1d4ed8' },
  twitter:   { bg: '#e0f2fe', color: '#0369a1' },
  x:         { bg: '#e0f2fe', color: '#0369a1' },
  linkedin:  { bg: '#dbeafe', color: '#1e40af' },
  youtube:   { bg: '#fee2e2', color: '#b91c1c' },
  tiktok:    { bg: '#f0fdf4', color: '#166534' },
  email:     { bg: '#f3f4f6', color: '#4b5563' },
  whatsapp:  { bg: '#dcfce7', color: '#15803d' },
  website:   { bg: '#f1f5f9', color: '#475569' },
};

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const fmtNum = (n) =>
  n == null ? '—' : Number(n).toLocaleString();

// ── Badges & pills ───────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.planning;
  return (
    <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap"
          style={{ backgroundColor: m.bg, color: m.color }}>
      {m.label}
    </span>
  );
};

const ChannelPill = ({ channel }) => {
  const lc = (channel || '').toLowerCase();
  const { bg, color } = CHANNEL_COLORS[lc] || { bg: SYSTEM_COLORS.surfaceMuted, color: SYSTEM_COLORS.textMuted };
  return (
    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize whitespace-nowrap"
          style={{ backgroundColor: bg, color }}>
      {channel}
    </span>
  );
};

// ── Metrics panel ─────────────────────────────────────────────────────────────
const MetricsPanel = ({ promotionId }) => {
  const [metrics, setMetrics]   = useState(null);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const data = await projectPromotionsService.getMetrics(promotionId);
        if (mounted) setMetrics(data);
      } catch { /* show empty state */ }
      finally  { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [promotionId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="h-7 w-7 animate-spin rounded-full border-b-2"
             style={{ borderColor: BRAND_COLORS.primary }} />
      </div>
    );
  }

  if (!metrics) {
    return (
      <p className="text-sm text-center py-10" style={{ color: SYSTEM_COLORS.textMuted }}>
        Metrics unavailable — no published content linked yet.
      </p>
    );
  }

  const totals = metrics.totals || metrics; // handle flat or nested shape
  const KPI = [
    { label: 'Impressions',   value: totals.impressions    },
    { label: 'Likes',         value: totals.likes          },
    { label: 'Comments',      value: totals.comments       },
    { label: 'Shares',        value: totals.shares         },
    { label: 'Deliverables',  value: metrics.deliverablesCount ?? metrics.deliverables_count },
  ].filter(k => k.value != null);

  const byPlatform = metrics.byPlatform || metrics.by_platform || [];

  return (
    <div className="space-y-5">
      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {KPI.map(({ label, value }) => (
          <div key={label}
               className="rounded-xl border px-4 py-3 text-center"
               style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}>
            <p className="text-xl font-bold" style={{ color: BRAND_COLORS.primary }}>{fmtNum(value)}</p>
            <p className="text-xs mt-0.5" style={{ color: SYSTEM_COLORS.textMuted }}>{label}</p>
          </div>
        ))}
      </div>

      {/* Per-platform breakdown */}
      {byPlatform.length > 0 && (
        <div className="rounded-xl border overflow-hidden"
             style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
          <div className="px-4 py-3 border-b text-xs font-semibold uppercase tracking-wide"
               style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle, color: SYSTEM_COLORS.textMuted }}>
            By Platform
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
                <tr>
                  {['Platform', 'Impressions', 'Likes', 'Comments', 'Shares'].map(h => (
                    <th key={h} className="px-4 py-2 text-left text-xs font-medium"
                        style={{ color: SYSTEM_COLORS.textMuted }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
                {byPlatform.map((row, i) => (
                  <tr key={i}>
                    <td className="px-4 py-2">
                      <ChannelPill channel={row.platform} />
                    </td>
                    {['impressions','likes','comments','shares'].map(k => (
                      <td key={k} className="px-4 py-2 text-sm"
                          style={{ color: NEUTRAL_COLORS.black }}>
                        {fmtNum(row[k])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Linked entities panel ─────────────────────────────────────────────────────
const LinkedPanel = ({ promo, onUpdate }) => {
  const [contentIds,  setContentIds]  = useState('');
  const [assetIds,    setAssetIds]    = useState('');
  const [working,     setWorking]     = useState(null); // 'attach-c' | 'detach-c' | 'attach-a' | 'detach-a'

  const parseIds = (s) => s.split(',').map(v => v.trim()).filter(Boolean);

  const doAttachContent = async () => {
    const ids = parseIds(contentIds);
    if (!ids.length) { toast.error('Enter at least one content ID'); return; }
    setWorking('attach-c');
    try { onUpdate(await projectPromotionsService.attachContent(promo.id, ids)); toast.success('Content attached'); setContentIds(''); }
    catch (e) { toast.error(e?.message || 'Failed'); }
    finally { setWorking(null); }
  };

  const doDetachContent = async (id) => {
    setWorking(`detach-c-${id}`);
    try { onUpdate(await projectPromotionsService.detachContent(promo.id, [id])); toast.success('Detached'); }
    catch (e) { toast.error(e?.message || 'Failed'); }
    finally { setWorking(null); }
  };

  const doAttachAsset = async () => {
    const ids = parseIds(assetIds);
    if (!ids.length) { toast.error('Enter at least one request ID'); return; }
    setWorking('attach-a');
    try { onUpdate(await projectPromotionsService.attachAssetRequests(promo.id, ids)); toast.success('Asset requests attached'); setAssetIds(''); }
    catch (e) { toast.error(e?.message || 'Failed'); }
    finally { setWorking(null); }
  };

  const doDetachAsset = async (id) => {
    setWorking(`detach-a-${id}`);
    try { onUpdate(await projectPromotionsService.detachAssetRequests(promo.id, [id])); toast.success('Detached'); }
    catch (e) { toast.error(e?.message || 'Failed'); }
    finally { setWorking(null); }
  };

  const inp = 'flex-1 rounded-xl border px-3 py-2 text-sm outline-none';
  const is  = { borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle, color: NEUTRAL_COLORS.black };
  const sec = (t, count) => (
    <div className="flex items-center justify-between mb-2">
      <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: SYSTEM_COLORS.textMuted }}>{t}</p>
      <span className="text-xs font-semibold rounded-full px-2 py-0.5"
            style={{ backgroundColor: SYSTEM_COLORS.primaryTint, color: BRAND_COLORS.primary }}>
        {count}
      </span>
    </div>
  );

  const IdChip = ({ id, onDetach, wKey }) => (
    <div className="flex items-center justify-between px-2.5 py-1.5 rounded-lg"
         style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted }}>
      <span className="text-xs font-mono truncate mr-2" style={{ color: NEUTRAL_COLORS.black }}>{id}</span>
      <button onClick={() => onDetach(id)} disabled={working === wKey}
        className="flex-shrink-0 p-0.5 rounded hover:bg-red-50 disabled:opacity-40">
        <Unlink size={11} style={{ color: SYSTEM_COLORS.danger }} />
      </button>
    </div>
  );

  return (
    <div className="space-y-5">
      {/* Content Items */}
      <div className="rounded-xl border p-4 space-y-3"
           style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
        {sec('Content Items', promo.contentItemIds?.length || 0)}
        <div className="space-y-1.5">
          {promo.contentItemIds?.length ? (
            promo.contentItemIds.map(id => (
              <IdChip key={id} id={id} onDetach={doDetachContent} wKey={`detach-c-${id}`} />
            ))
          ) : (
            <p className="text-xs" style={{ color: SYSTEM_COLORS.textSoft }}>No content linked yet.</p>
          )}
        </div>
        <div className="flex gap-2 pt-1">
          <input type="text" value={contentIds} onChange={e => setContentIds(e.target.value)}
            placeholder="Content IDs, comma-separated" className={inp} style={is} />
          <button onClick={doAttachContent} disabled={working === 'attach-c'}
            className="inline-flex items-center gap-1 px-3 py-2 text-xs font-semibold rounded-xl text-white disabled:opacity-50 flex-shrink-0"
            style={{ backgroundColor: BRAND_COLORS.primary }}>
            <Link2 size={12} /> Attach
          </button>
        </div>
      </div>

      {/* Asset Requests */}
      <div className="rounded-xl border p-4 space-y-3"
           style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle }}>
        {sec('Asset Requests', promo.assetRequestIds?.length || 0)}
        <div className="space-y-1.5">
          {promo.assetRequestIds?.length ? (
            promo.assetRequestIds.map(id => (
              <IdChip key={id} id={id} onDetach={doDetachAsset} wKey={`detach-a-${id}`} />
            ))
          ) : (
            <p className="text-xs" style={{ color: SYSTEM_COLORS.textSoft }}>No asset requests linked yet.</p>
          )}
        </div>
        <div className="flex gap-2 pt-1">
          <input type="text" value={assetIds} onChange={e => setAssetIds(e.target.value)}
            placeholder="Asset request IDs, comma-separated" className={inp} style={is} />
          <button onClick={doAttachAsset} disabled={working === 'attach-a'}
            className="inline-flex items-center gap-1 px-3 py-2 text-xs font-semibold rounded-xl text-white disabled:opacity-50 flex-shrink-0"
            style={{ backgroundColor: BRAND_COLORS.primary }}>
            <Link2 size={12} /> Attach
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Promotion Form Modal ──────────────────────────────────────────────────────
const EMPTY_FORM = {
  projectId: '', title: '', goal: '', targetAudience: '',
  keyMessages: '', channels: [], startDate: '', endDate: '',
  budget: '', status: 'planning', ownerId: '',
};

const PromotionFormModal = ({ initial, onClose, onSaved }) => {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(
    initial
      ? {
          ...EMPTY_FORM, ...initial,
          keyMessages: (initial.keyMessages || []).join('\n'),
          channels:    initial.channels || [],
          startDate:   initial.startDate ? initial.startDate.split('T')[0] : '',
          endDate:     initial.endDate   ? initial.endDate.split('T')[0]   : '',
          budget:      initial.budget != null ? String(initial.budget) : '',
        }
      : { ...EMPTY_FORM }
  );
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleChannel = (ch) => {
    set('channels', form.channels.includes(ch)
      ? form.channels.filter(c => c !== ch)
      : [...form.channels, ch]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (!isEdit && !form.projectId.trim()) { toast.error('Project ID is required'); return; }
    setSaving(true);
    try {
      const payload = {
        title:          form.title.trim(),
        goal:           form.goal.trim()           || undefined,
        targetAudience: form.targetAudience.trim() || undefined,
        keyMessages:    form.keyMessages.trim()
          ? form.keyMessages.split('\n').map(s => s.trim()).filter(Boolean)
          : undefined,
        channels:       form.channels.length ? form.channels : undefined,
        startDate:      form.startDate  || undefined,
        endDate:        form.endDate    || undefined,
        budget:         form.budget !== '' ? Number(form.budget) : undefined,
        status:         form.status,
        ownerId:        form.ownerId.trim() || undefined,
        ...(!isEdit && { projectId: form.projectId.trim() }),
      };
      const saved = isEdit
        ? await projectPromotionsService.update(initial.id, payload)
        : await projectPromotionsService.create(payload);
      toast.success(isEdit ? 'Promotion updated' : 'Promotion created');
      onSaved(saved);
      onClose();
    } catch (err) {
      toast.error(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const lbl = 'block text-xs font-semibold mb-1.5';
  const inp = 'w-full rounded-xl border px-3 py-2 text-sm outline-none';
  const is  = { borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.surfaceSubtle, color: NEUTRAL_COLORS.black };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 sm:p-6 overflow-y-auto"
         style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
      <div className="relative w-full max-w-2xl rounded-2xl shadow-xl my-8"
           style={{ backgroundColor: SYSTEM_COLORS.shellSurface }}>
        <div className="flex items-center justify-between px-6 py-4 border-b"
             style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
          <h2 className="text-base font-bold" style={{ color: NEUTRAL_COLORS.black }}>
            {isEdit ? 'Edit Promotion' : 'New Project Promotion'}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100">
            <X size={18} style={{ color: SYSTEM_COLORS.textMuted }} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {!isEdit && (
            <div>
              <label className={lbl} style={{ color: SYSTEM_COLORS.textMuted }}>Project ID *</label>
              <input type="text" value={form.projectId} onChange={e => set('projectId', e.target.value)}
                placeholder="Linked project ID" className={inp} style={is} />
            </div>
          )}

          <div>
            <label className={lbl} style={{ color: SYSTEM_COLORS.textMuted }}>Title *</label>
            <input type="text" value={form.title} onChange={e => set('title', e.target.value)}
              placeholder="e.g. UDSM Open Day 2026 Launch Campaign" className={inp} style={is} />
          </div>

          <div>
            <label className={lbl} style={{ color: SYSTEM_COLORS.textMuted }}>Goal</label>
            <textarea value={form.goal} onChange={e => set('goal', e.target.value)}
              rows={2} placeholder="What this promotion aims to achieve…"
              className={inp} style={{ ...is, resize: 'vertical' }} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={lbl} style={{ color: SYSTEM_COLORS.textMuted }}>Target Audience</label>
              <input type="text" value={form.targetAudience} onChange={e => set('targetAudience', e.target.value)}
                placeholder="Who are we reaching?" className={inp} style={is} />
            </div>
            <div>
              <label className={lbl} style={{ color: SYSTEM_COLORS.textMuted }}>Owner ID</label>
              <input type="text" value={form.ownerId} onChange={e => set('ownerId', e.target.value)}
                placeholder="User ID (default: you)" className={inp} style={is} />
            </div>
          </div>

          <div>
            <label className={lbl} style={{ color: SYSTEM_COLORS.textMuted }}>Key Messages (one per line)</label>
            <textarea value={form.keyMessages} onChange={e => set('keyMessages', e.target.value)}
              rows={3} placeholder={"Brand awareness\nStudent registration opens\nVisit us at main campus"}
              className={inp} style={{ ...is, resize: 'vertical' }} />
          </div>

          {/* Channels */}
          <div>
            <label className={lbl} style={{ color: SYSTEM_COLORS.textMuted }}>Channels</label>
            <div className="flex flex-wrap gap-2">
              {COMMON_CHANNELS.map(ch => {
                const active = form.channels.includes(ch);
                const { bg, color } = CHANNEL_COLORS[ch] || { bg: SYSTEM_COLORS.surfaceMuted, color: SYSTEM_COLORS.textMuted };
                return (
                  <button type="button" key={ch} onClick={() => toggleChannel(ch)}
                    className="px-3 py-1.5 rounded-xl text-xs font-semibold capitalize transition-colors border"
                    style={{
                      backgroundColor: active ? bg : 'transparent',
                      color:           active ? color : SYSTEM_COLORS.textMuted,
                      borderColor:     active ? color : SYSTEM_COLORS.borderSoft,
                    }}>
                    {ch}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={lbl} style={{ color: SYSTEM_COLORS.textMuted }}>Start Date</label>
              <input type="date" value={form.startDate} onChange={e => set('startDate', e.target.value)}
                className={inp} style={is} />
            </div>
            <div>
              <label className={lbl} style={{ color: SYSTEM_COLORS.textMuted }}>End Date</label>
              <input type="date" value={form.endDate} onChange={e => set('endDate', e.target.value)}
                className={inp} style={is} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={lbl} style={{ color: SYSTEM_COLORS.textMuted }}>Budget ($)</label>
              <input type="number" min="0" value={form.budget} onChange={e => set('budget', e.target.value)}
                placeholder="0" className={inp} style={is} />
            </div>
            <div>
              <label className={lbl} style={{ color: SYSTEM_COLORS.textMuted }}>Status</label>
              <select value={form.status} onChange={e => set('status', e.target.value)}
                className={inp} style={is}>
                {PROMOTION_STATUSES.map(s => (
                  <option key={s} value={s} className="capitalize">{STATUS_META[s]?.label || s}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm font-medium rounded-xl border"
              style={{ borderColor: SYSTEM_COLORS.borderSoft, color: SYSTEM_COLORS.textMuted }}>
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="px-5 py-2 text-sm font-semibold rounded-xl text-white disabled:opacity-50"
              style={{ backgroundColor: BRAND_COLORS.primary }}>
              {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Promotion'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Detail Modal ──────────────────────────────────────────────────────────────
const DetailModal = ({ promo: initPromo, onClose, onUpdated, onDeleted, onEdit }) => {
  const [promo,        setPromo]        = useState(initPromo);
  const [activeTab,    setActiveTab]    = useState('brief');
  const [deleteConf,   setDeleteConf]   = useState(false);
  const [deleting,     setDeleting]     = useState(false);

  const update = (updated) => { setPromo(updated); onUpdated(updated); };

  const doDelete = async () => {
    setDeleting(true);
    try {
      await projectPromotionsService.delete(promo.id);
      toast.success('Promotion deleted');
      onDeleted(promo.id);
      onClose();
    } catch (e) { toast.error(e?.message || 'Failed'); setDeleting(false); }
  };

  const DTABS = [
    { id: 'brief',   label: 'Brief'                              },
    { id: 'linked',  label: `Linked (${(promo.contentItemIds?.length || 0) + (promo.assetRequestIds?.length || 0)})` },
    { id: 'metrics', label: 'Metrics'                            },
  ];

  const sec = (label) => (
    <p className="text-xs font-semibold uppercase tracking-wide mb-2"
       style={{ color: SYSTEM_COLORS.textMuted }}>{label}</p>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 sm:p-6 overflow-y-auto"
         style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
      <div className="relative w-full max-w-2xl rounded-2xl shadow-xl my-8"
           style={{ backgroundColor: SYSTEM_COLORS.shellSurface }}>
        {/* Header */}
        <div className="px-6 py-4 border-b" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <StatusBadge status={promo.status} />
                {promo.projectId && (
                  <span className="text-xs rounded-full px-2.5 py-0.5 font-medium"
                        style={{ backgroundColor: SYSTEM_COLORS.infoTint, color: SYSTEM_COLORS.info }}>
                    Project: {promo.projectId}
                  </span>
                )}
              </div>
              <h2 className="text-base font-bold leading-snug" style={{ color: NEUTRAL_COLORS.black }}>
                {promo.title}
              </h2>
              <p className="text-xs mt-1" style={{ color: SYSTEM_COLORS.textMuted }}>
                {promo.startDate && promo.endDate
                  ? `${fmtDate(promo.startDate)} → ${fmtDate(promo.endDate)}`
                  : promo.startDate ? `From ${fmtDate(promo.startDate)}` : 'No dates set'}
                {promo.budget > 0 ? ` · $${fmtNum(promo.budget)}` : ''}
              </p>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button onClick={() => { onClose(); onEdit(promo); }}
                className="p-1.5 rounded-lg hover:bg-gray-100" title="Edit">
                <Edit2 size={15} style={{ color: SYSTEM_COLORS.textMuted }} />
              </button>
              <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100">
                <X size={18} style={{ color: SYSTEM_COLORS.textMuted }} />
              </button>
            </div>
          </div>
          <div className="flex gap-1 mt-3">
            {DTABS.map(({ id, label }) => {
              const active = activeTab === id;
              return (
                <button key={id} onClick={() => setActiveTab(id)}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
                  style={{
                    backgroundColor: active ? BRAND_COLORS.primary : 'transparent',
                    color: active ? '#fff' : SYSTEM_COLORS.textMuted,
                  }}>
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* ── Brief ───────────────────────────────────────────── */}
          {activeTab === 'brief' && (
            <>
              {promo.goal && (
                <div>
                  {sec('Goal')}
                  <p className="text-sm leading-relaxed" style={{ color: NEUTRAL_COLORS.black }}>{promo.goal}</p>
                </div>
              )}
              {promo.targetAudience && (
                <div>
                  {sec('Target Audience')}
                  <p className="text-sm" style={{ color: NEUTRAL_COLORS.black }}>{promo.targetAudience}</p>
                </div>
              )}
              {promo.keyMessages?.length > 0 && (
                <div>
                  {sec('Key Messages')}
                  <ul className="space-y-1">
                    {promo.keyMessages.map((m, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm"
                          style={{ color: NEUTRAL_COLORS.black }}>
                        <span className="flex-shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full"
                              style={{ backgroundColor: BRAND_COLORS.primary }} />
                        {m}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {promo.channels?.length > 0 && (
                <div>
                  {sec('Channels')}
                  <div className="flex flex-wrap gap-2">
                    {promo.channels.map(ch => <ChannelPill key={ch} channel={ch} />)}
                  </div>
                </div>
              )}
              {promo.ownerId && (
                <div>
                  {sec('Owner')}
                  <p className="text-sm font-mono" style={{ color: NEUTRAL_COLORS.black }}>{promo.ownerId}</p>
                </div>
              )}
              {!promo.goal && !promo.targetAudience && !promo.keyMessages?.length && (
                <p className="text-sm text-center py-8" style={{ color: SYSTEM_COLORS.textMuted }}>
                  No brief details — click Edit to fill in the brief.
                </p>
              )}
              {/* Delete zone */}
              <div className="rounded-xl border p-4 mt-2"
                   style={{ borderColor: SYSTEM_COLORS.dangerTint, backgroundColor: SYSTEM_COLORS.dangerTint }}>
                {deleteConf ? (
                  <div className="flex items-center gap-3">
                    <p className="flex-1 text-xs font-medium" style={{ color: SYSTEM_COLORS.danger }}>
                      Delete this promotion permanently?
                    </p>
                    <button onClick={doDelete} disabled={deleting}
                      className="px-3 py-1.5 text-xs font-semibold rounded-xl text-white disabled:opacity-50"
                      style={{ backgroundColor: SYSTEM_COLORS.danger }}>
                      {deleting ? 'Deleting…' : 'Confirm'}
                    </button>
                    <button onClick={() => setDeleteConf(false)}
                      className="px-3 py-1.5 text-xs rounded-xl border"
                      style={{ borderColor: SYSTEM_COLORS.danger, color: SYSTEM_COLORS.danger }}>
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button onClick={() => setDeleteConf(true)}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold"
                    style={{ color: SYSTEM_COLORS.danger }}>
                    <Trash2 size={13} /> Delete promotion
                  </button>
                )}
              </div>
            </>
          )}

          {/* ── Linked ──────────────────────────────────────────── */}
          {activeTab === 'linked' && (
            <LinkedPanel promo={promo} onUpdate={update} />
          )}

          {/* ── Metrics ─────────────────────────────────────────── */}
          {activeTab === 'metrics' && (
            <MetricsPanel promotionId={promo.id} />
          )}
        </div>
      </div>
    </div>
  );
};

// ── Promotion card ────────────────────────────────────────────────────────────
const PromotionCard = ({ promo, onClick }) => {
  const hasDate = promo.startDate || promo.endDate;
  const channelShow = (promo.channels || []).slice(0, 3);
  const extraCh     = (promo.channels || []).length - channelShow.length;

  return (
    <div
      className="rounded-xl border p-4 cursor-pointer space-y-3 hover:shadow-sm transition-shadow"
      style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}
      onClick={onClick}
    >
      {/* Status + project */}
      <div className="flex items-center justify-between gap-2">
        <StatusBadge status={promo.status} />
        {promo.projectId && (
          <span className="text-xs truncate max-w-[120px]" style={{ color: SYSTEM_COLORS.textMuted }}>
            {promo.projectId}
          </span>
        )}
      </div>

      {/* Title */}
      <h3 className="text-sm font-semibold leading-snug line-clamp-2"
          style={{ color: NEUTRAL_COLORS.black }}>
        {promo.title}
      </h3>

      {/* Goal */}
      {promo.goal && (
        <p className="text-xs leading-relaxed line-clamp-2" style={{ color: SYSTEM_COLORS.textSoft }}>
          {promo.goal}
        </p>
      )}

      {/* Channels */}
      {channelShow.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {channelShow.map(ch => <ChannelPill key={ch} channel={ch} />)}
          {extraCh > 0 && (
            <span className="text-xs self-center" style={{ color: SYSTEM_COLORS.textMuted }}>
              +{extraCh}
            </span>
          )}
        </div>
      )}

      {/* Footer meta */}
      <div className="flex items-center justify-between pt-1 border-t"
           style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
        <div className="text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>
          {hasDate
            ? `${fmtDate(promo.startDate)} → ${fmtDate(promo.endDate)}`
            : 'No dates set'}
        </div>
        <div className="flex items-center gap-3 text-xs" style={{ color: SYSTEM_COLORS.textSoft }}>
          {promo.contentItemIds?.length > 0 && (
            <span>{promo.contentItemIds.length} content</span>
          )}
          {promo.assetRequestIds?.length > 0 && (
            <span>{promo.assetRequestIds.length} assets</span>
          )}
          {promo.budget > 0 && (
            <span>${fmtNum(promo.budget)}</span>
          )}
        </div>
      </div>
    </div>
  );
};

// ── ProjectPromotions ─────────────────────────────────────────────────────────
const ProjectPromotions = () => {
  const [promotions,   setPromotions]   = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [statusFilter, setStatusFilter] = useState(null);
  const [detail,       setDetail]       = useState(null);
  const [formModal,    setFormModal]    = useState(null); // null | 'new' | promo

  const fetchPromotions = useCallback(async () => {
    setLoading(true);
    try {
      const data = await projectPromotionsService.list(
        statusFilter ? { status: [statusFilter] } : {}
      );
      setPromotions(data || []);
    } catch (err) {
      toast.error(err?.message || 'Failed to load promotions');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchPromotions(); }, [fetchPromotions]);

  const handleSaved = (saved) => {
    setPromotions(prev => {
      const idx = prev.findIndex(p => p.id === saved.id);
      return idx >= 0 ? prev.map((p, i) => (i === idx ? saved : p)) : [saved, ...prev];
    });
  };

  const handleUpdated = (updated) => {
    setPromotions(prev => prev.map(p => p.id === updated.id ? updated : p));
    setDetail(updated);
  };

  const handleDeleted = (id) => {
    setPromotions(prev => prev.filter(p => p.id !== id));
  };

  // KPI
  const counts = PROMOTION_STATUSES.reduce((acc, s) => {
    acc[s] = promotions.filter(p => p.status === s).length;
    return acc;
  }, {});

  return (
    <div className="space-y-5">
      {/* ── Top bar ──────────────────────────────────────────────── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {/* KPI chips */}
        <div className="flex items-center gap-2 flex-wrap">
          {[
            { label: 'Total',     value: promotions.length, color: NEUTRAL_COLORS.black  },
            { label: 'Planning',  value: counts.planning,   color: SYSTEM_COLORS.textMuted },
            { label: 'Active',    value: counts.active,     color: SYSTEM_COLORS.success  },
            { label: 'Paused',    value: counts.paused,     color: SYSTEM_COLORS.warning  },
            { label: 'Completed', value: counts.completed,  color: SYSTEM_COLORS.info     },
          ].map(({ label, value, color }) => (
            <div key={label}
                 className="flex items-center gap-1.5 px-3 py-2 rounded-xl border"
                 style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}>
              <span className="text-base font-bold" style={{ color }}>{value}</span>
              <span className="text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>{label}</span>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button onClick={fetchPromotions}
            className="p-2 rounded-xl border"
            style={{ borderColor: SYSTEM_COLORS.borderSoft, backgroundColor: SYSTEM_COLORS.shellSurface }}>
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''}
                       style={{ color: SYSTEM_COLORS.textMuted }} />
          </button>
          <button onClick={() => setFormModal('new')}
            className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-semibold text-white"
            style={{ backgroundColor: BRAND_COLORS.primary }}>
            <Plus size={14} /> New Promotion
          </button>
        </div>
      </div>

      {/* ── Status filter pills ───────────────────────────────────── */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        {[null, ...PROMOTION_STATUSES].map(s => {
          const active = statusFilter === s;
          const meta   = s ? STATUS_META[s] : null;
          return (
            <button key={s ?? 'all'}
              onClick={() => setStatusFilter(s === statusFilter ? null : s)}
              className="flex-shrink-0 px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-colors"
              style={{
                backgroundColor: active ? BRAND_COLORS.primary : SYSTEM_COLORS.shellSurface,
                color: active ? '#fff' : SYSTEM_COLORS.textMuted,
                border: `1px solid ${active ? 'transparent' : SYSTEM_COLORS.borderSoft}`,
              }}>
              {s ? `${meta.label} (${counts[s]})` : 'All'}
            </button>
          );
        })}
      </div>

      {/* ── Grid ─────────────────────────────────────────────────── */}
      {loading ? (
        <div className="flex items-center justify-center h-48 rounded-xl border"
             style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
          <div className="h-8 w-8 animate-spin rounded-full border-b-2"
               style={{ borderColor: BRAND_COLORS.primary }} />
        </div>
      ) : promotions.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed py-20"
             style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
          <Target size={32} style={{ color: SYSTEM_COLORS.textSoft }} />
          <p className="text-sm font-medium" style={{ color: SYSTEM_COLORS.textMuted }}>
            No promotions yet
          </p>
          <button onClick={() => setFormModal('new')}
            className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-semibold text-white"
            style={{ backgroundColor: BRAND_COLORS.primary }}>
            <Plus size={14} /> Create First Promotion
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {promotions.map(p => (
            <PromotionCard key={p.id} promo={p} onClick={() => setDetail(p)} />
          ))}
        </div>
      )}

      {/* ── Modals ──────────────────────────────────────────────── */}
      {formModal && (
        <PromotionFormModal
          initial={formModal === 'new' ? null : formModal}
          onClose={() => setFormModal(null)}
          onSaved={handleSaved}
        />
      )}
      {detail && (
        <DetailModal
          promo={detail}
          onClose={() => setDetail(null)}
          onUpdated={handleUpdated}
          onDeleted={handleDeleted}
          onEdit={(p) => { setDetail(null); setFormModal(p); }}
        />
      )}
    </div>
  );
};

export default ProjectPromotions;
